import type { Evidence } from "../api/types";
import { HashChip } from "../compose/controls";
import { CheckCircle2, ShieldCheck } from "../compose/Icon";
import { useToast } from "../compose/Toast";
import { roleLabel } from "../lib/format";

/**
 * Delivery evidence — the product's UVP made visible (ia-and-flows §7,
 * "the gate cannot lie"). Shows the head content hash and every REQUIRED
 * reviewer's verdict pinned to that exact hash. A verdict that is not `pass`
 * renders honestly, never green.
 *
 * The bundle is SERVER-DERIVED at read time from `el.review_verdicts`
 * (`api.delivery_service.evidenceFor`), so what this renders is the same
 * predicate `GET /api/documents/:id/export` enforces. In particular `blocked`
 * comes FROM THE SERVER — this component does not recompute the gate from the
 * verdict rows, because architecture invariant 6 forbids the delivery decision
 * being asserted from the client. (It previously derived `allPass` locally from
 * a frozen `el.delivery_evidence` projection, which is how the panel came to
 * claim "gate passed" on documents export was simultaneously 403-ing.)
 *
 * A required reviewer holding no verdict at the head hash arrives as `pending`.
 */
export function EvidencePanel({ evidence }: { evidence: Evidence }) {
  const toast = useToast();
  const passCount = evidence.verdicts.filter(
    (v) => v.verdict.toLowerCase() === "pass",
  ).length;
  // The SERVER's decision, not ours: `blocked === false` ⇔ export would ship.
  const gatePassed = !evidence.blocked;

  return (
    <section className="evidence" data-testid="evidence-panel">
      <h3 className="evidence__h">
        <ShieldCheck size={16} strokeWidth={2} />
        {gatePassed ? "Delivered · gate passed" : "Delivery evidence"}
      </h3>
      <p className="evidence__note" data-testid="evidence-summary">
        {passCount} of {evidence.verdicts.length} required reviewers passed on
        the current content hash.{" "}
        {gatePassed
          ? "Delivery can’t lie — each verdict is pinned to the exact text that shipped."
          : "Export is blocked until every required reviewer passes on this exact hash."}
      </p>

      <div className="evidence__hashlabel">Content hash</div>
      <HashChip hash={evidence.hash} onCopy={() => toast.show("Hash copied")} />

      <div className="evidence__verdicts" data-testid="verdict-list">
        {evidence.verdicts.map((v) => {
          const pass = v.verdict.toLowerCase() === "pass";
          return (
            <div className="verdict" key={v.reviewer} data-testid="verdict-row">
              {pass ? (
                <span className="verdict__pass" aria-label="pass">
                  <CheckCircle2 size={16} strokeWidth={2} />
                </span>
              ) : (
                <span aria-label={v.verdict}>•</span>
              )}
              <span className="verdict__reviewer">{roleLabel(v.reviewer)}</span>
              <span className="verdict__on">
                {pass ? `PASS · ${evidence.hash.slice(0, 8)}…` : v.verdict}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
